import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, of, tap } from 'rxjs';

export type Theme = 'light' | 'dark';

/**
 * SCRUM-764 — theme preference is persisted per user across sessions.
 * Storage layers, in priority order:
 *   1. user_preferences.theme  (server, authoritative once user is logged in)
 *   2. localStorage 'hub-theme' (fast-paint cache so the first render is correct)
 *   3. prefers-color-scheme    (default for fresh users)
 *
 * Flow:
 *   - On boot the constructor applies localStorage immediately so there is
 *     no flash of wrong theme.
 *   - Post-auth callers invoke loadUserPreference() to reconcile with server.
 *   - On toggle we update DOM + localStorage immediately and fire-and-forget
 *     PATCH /auth/preferences so the choice persists across devices.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly http = inject(HttpClient);
  private readonly themeSubject = new BehaviorSubject<Theme>(this.getInitialTheme());
  private readonly theme$ = this.themeSubject.asObservable();

  constructor() {
    this.applyTheme(this.themeSubject.value);
  }

  getTheme(): Observable<Theme> {
    return this.theme$;
  }

  getCurrentTheme(): Theme {
    return this.themeSubject.value;
  }

  toggleTheme(): void {
    const newTheme: Theme = this.themeSubject.value === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme, { persistRemote: true });
  }

  setTheme(theme: Theme, opts: { persistRemote?: boolean } = {}): void {
    this.themeSubject.next(theme);
    localStorage.setItem('hub-theme', theme);
    this.applyTheme(theme);

    if (opts.persistRemote) {
      // Failure (offline, 401 before login) is non-fatal — localStorage already
      // captured the choice and the next successful save will reconcile.
      this.http.patch('/api/auth/preferences', { theme }).pipe(
        catchError(() => of(null)),
      ).subscribe();
    }
  }

  /**
   * Reconcile local state with the authoritative server preference. Call after
   * login. If the server value differs from the locally-applied one, the DOM
   * updates immediately. If the user has never set a preference, the local
   * value is left in place and will be pushed on the next toggle.
   */
  loadUserPreference(): Observable<Theme | null> {
    return this.http.get<{ theme: Theme | null }>('/api/auth/preferences').pipe(
      tap(prefs => {
        if (prefs?.theme && prefs.theme !== this.themeSubject.value) {
          this.themeSubject.next(prefs.theme);
          localStorage.setItem('hub-theme', prefs.theme);
          this.applyTheme(prefs.theme);
        }
      }),
      tap(prefs => prefs?.theme),
      catchError(() => of(null)),
    ) as Observable<Theme | null>;
  }

  private getInitialTheme(): Theme {
    const stored = localStorage.getItem('hub-theme') as Theme | null;
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    if (globalThis.matchMedia?.('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  }

  private applyTheme(theme: Theme): void {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      root.style.colorScheme = 'light';
    }
  }
}
